import React, { memo, ReactNode, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { QuoteContext } from '../../../contexts/Quote';
import { Theme } from '../../../interfaces/enums/Theme.enum';
import { Clause } from '../../../services/quote.interfaces';
import {
  getQuoteModalState,
  setModalState,
} from '../../../store/slices/quote/quote.store';
import { withBlokWrapper } from '../../BlokWrapper/BlokWrapper';
import QuoteFormHeadingBar from '../../QuoteFormHeadingBar/QuoteFormHeadingBar';
import Col from '../../Utils/Col/Col';
import Container from '../../Utils/Container/Container';
import Grid from '../../Utils/Grid/Grid';
import QuoteStageModal from '../QuoteStageModal/QuoteStageModal';

const mailIconStyle = {
  WebkitMaskImage: "url('/svgs/blue-mail-icon.svg')",
  maskImage: "url('/svgs/blue-mail-icon.svg')",
};

interface QuoteStageMainLayoutProps {
  heading: string;
  leftColumn: ReactNode;
  rightColumn: ReactNode;
}

const QuoteStageMainLayout: React.FC<QuoteStageMainLayoutProps> = ({
  heading,
  leftColumn,
  rightColumn,
}) => {
  const dispatch = useDispatch();

  const modalState = useSelector(getQuoteModalState);
  const { quoteDetails, quoteCostBreakdown } = useContext(QuoteContext);

  const clauses: any = quoteCostBreakdown?.clauses;

  const termsAndConditionsContent = useMemo(() => {
    return (
      clauses &&
      clauses.map((clause: Clause) => {
        return (
          <div className="modal-item">
            <h3 className="text-system/white text-2xl">{clause.heading}</h3>
            <p className="text-system/white text-lg">{clause.description}</p>
          </div>
        );
      })
    );
  }, [clauses]);

  const saveForLaterContent = useMemo(() => {
    return (
      <div className="modal-item flex flex-col items-center gap-6">
        <i
          className="h-[42px] w-[54px] bg-no-repeat bg-[#00B5FF]"
          style={mailIconStyle}
        ></i>
        <h3 className="text-system/white text-2xl text-center">
          Thank you for your quote, {quoteDetails?.customerDetails?.firstName}.
        </h3>
        <p className="text-system/white text-md text-center flex flex-col items-center">
          <span>
            Your quote reference has been saved and a copy has been emailed to
            you. To retrieve your quotation, click the link included in the
            email. Your quote reference is
          </span>
          <span className="text-[#00B5FF]">{quoteDetails?.quoteReference}</span>
        </p>
      </div>
    );
  }, []);

  const modalContentMap = new Map<string, ReactNode>([
    ['termsAndConditions', termsAndConditionsContent],
    ['saveForLater', saveForLaterContent],
  ]);

  return (
    <section className="xl:pb-20">
      <Container startingBreakpoint="sm">
        {heading && (
          <QuoteFormHeadingBar
            heading={heading}
            isBottomBorderRequired={false}
          />
        )}
        <Grid gapY="gap-y-0">
          <Col
            span={{ xl: 6, xxl: 5 }}
            start={{ xxl: 2 }}
            order={{ default: 2, xl: 1 }}
          >
            {leftColumn}
          </Col>
          <Col
            span={{ xl: 5, xxl: 4 }}
            start={{ xl: 8, xxl: 8 }}
            order={{ default: 1, xl: 2 }}
          >
            {rightColumn}
          </Col>
        </Grid>
      </Container>

      <QuoteStageModal
        onClose={() =>
          dispatch(
            setModalState({
              isQuoteModalOpen: false,
              quoteModalContent: null,
            })
          )
        }
        toggleModal={modalState.isQuoteModalOpen}
      >
        <div className="flex flex-col gap-6">
          {modalContentMap.get(modalState.quoteModalContent!)}
        </div>
      </QuoteStageModal>
    </section>
  );
};

export default memo(withBlokWrapper(QuoteStageMainLayout, Theme.ALT));
